import { applyCleValidationErrorsToFormik } from "@/api/cle-api/errors";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorAlert } from "@/components/cle-api/CleApiErrorAlert";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import { FormikDatePicker } from "@/components/formcontrols/DatePicker";
import Modal, { ModalContext } from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import type {
  InsurancePolicyModel,
  UpdateInsurancePolicyRequest,
} from "@/api/cle-api";
import { useUpdateInsurancePolicy } from "@/hooks/cle-hooks";
import { dateToyyyyMMdd } from "@/util/date";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext } from "react";
import * as Yup from "yup";

export interface InsurancePolicyValidationFormValues {
  insuranceCompany: string;
  policyStartDate: Date | null;
  lawyerLiabilityAmount: string;
  guaranteeAmount: string;
}

const validationSchema = Yup.object({
  insuranceCompany: Yup.string().trim(),
  policyStartDate: Yup.date().nullable(),
  lawyerLiabilityAmount: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .integer("Beløbet skal være et helt tal")
    .typeError("Beløbet skal være et tal"),
  guaranteeAmount: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .integer("Beløbet skal være et helt tal")
    .typeError("Beløbet skal være et tal"),
});

export const policyToValidationFormValues = (
  policy: InsurancePolicyModel,
): InsurancePolicyValidationFormValues => ({
  insuranceCompany: policy.insuranceCompany ?? "",
  policyStartDate: policy.policyStartDate
    ? new Date(policy.policyStartDate)
    : null,
  lawyerLiabilityAmount:
    policy.lawyerLiabilityAmount != null
      ? String(policy.lawyerLiabilityAmount)
      : "",
  guaranteeAmount:
    policy.guaranteeAmount != null ? String(policy.guaranteeAmount) : "",
});

export const validationFormValuesToUpdateRequest = (
  values: InsurancePolicyValidationFormValues,
): UpdateInsurancePolicyRequest => ({
  insurance_company: values.insuranceCompany.trim() || null,
  policy_start_date: values.policyStartDate
    ? dateToyyyyMMdd(values.policyStartDate)
    : null,
  lawyer_liability_amount:
    values.lawyerLiabilityAmount === ""
      ? null
      : Number(values.lawyerLiabilityAmount),
  guarantee_amount:
    values.guaranteeAmount === "" ? null : Number(values.guaranteeAmount),
});

interface InsurancePolicyValidationEditFormProps {
  policy: InsurancePolicyModel;
  onSave: (updated: InsurancePolicyModel) => void;
}

const InsurancePolicyValidationEditForm = ({
  policy,
  onSave,
}: InsurancePolicyValidationEditFormProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate: updatePolicy,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useUpdateInsurancePolicy();

  const formik = useFormik<InsurancePolicyValidationFormValues>({
    initialValues: policyToValidationFormValues(policy),
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      updatePolicy(
        {
          id: policy.id,
          userId: policy.ownerUserId,
          body: validationFormValuesToUpdateRequest(values),
        },
        {
          onSuccess: (updated) => {
            onSave(updated);
            resetMutation();
            setModalOpen(false);
          },
          onError: (err) => {
            applyCleValidationErrorsToFormik(err, formik.setFieldError);
          },
        },
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-item-list c-modal-container-inner">
            <div className="flex-100">
              <label className="form-label" htmlFor="insuranceCompany">
                Forsikringsselskab
              </label>
              <input
                autoComplete="off"
                className="form-control"
                id="insuranceCompany"
                name="insuranceCompany"
                type="text"
                value={formik.values.insuranceCompany}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <FormikDatePicker
                formik={formik}
                config={{
                  component: "datepicker",
                  id: "policyStartDate",
                  label: "Policens startdato",
                  initialValue: "",
                  isRequired: false,
                }}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="lawyerLiabilityAmount">
                Advokatansvar (beløb)
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.lawyerLiabilityAmount &&
                  formik.errors.lawyerLiabilityAmount
                    ? " is-invalid"
                    : ""
                }`}
                id="lawyerLiabilityAmount"
                min="0"
                name="lawyerLiabilityAmount"
                step="1"
                type="number"
                value={formik.values.lawyerLiabilityAmount}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.lawyerLiabilityAmount}
                errorMessage={formik.errors.lawyerLiabilityAmount}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="guaranteeAmount">
                Garantibeløb
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.guaranteeAmount &&
                  formik.errors.guaranteeAmount
                    ? " is-invalid"
                    : ""
                }`}
                id="guaranteeAmount"
                min="0"
                name="guaranteeAmount"
                step="1"
                type="number"
                value={formik.values.guaranteeAmount}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.guaranteeAmount}
                errorMessage={formik.errors.guaranteeAmount}
              />
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner">
              <CleApiErrorAlert
                error={error}
                fallback="Kunne ikke gemme validering."
              />
            </div>
          )}
          <StickyFooter className="border-top">
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button color="primary" disabled={isPending} type="submit">
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

interface InsurancePolicyValidationEditModalProps {
  policy: InsurancePolicyModel;
  onSave: (updated: InsurancePolicyModel) => void;
}

export const InsurancePolicyValidationEditModal = ({
  policy,
  onSave,
}: InsurancePolicyValidationEditModalProps) => (
  <Modal title="Redigér validering">
    <InsurancePolicyValidationEditForm policy={policy} onSave={onSave} />
  </Modal>
);
